import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

import { colors, spacing, typo, paperSx } from '../theme/tokens';

interface Props {
  missing: string[];
}

/**
 * Rendered when auth is enabled (REACT_APP_AUTH0_CLIENT_ID is set) but other
 * required Auth0 env vars are missing. Better to fail loud at boot than mint
 * tokens with a wrong audience and see every backend request 401.
 */
const AuthConfigErrorPage: React.FC<Props> = ({ missing }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgPage,
      p: spacing.xxxl,
    }}
  >
    <Paper elevation={0} sx={{ ...paperSx, maxWidth: 640, width: '100%', mb: 0 }}>
      <Typography sx={{ ...typo.pageTitle, mb: spacing.lg }}>
        Authentication not configured
      </Typography>
      <Typography sx={{ ...typo.bodyText, color: colors.textSecondary, mb: spacing.xl }}>
        Auth0 is partially configured. The following environment variables are required but
        unset:
      </Typography>
      <Box component="ul" sx={{ pl: spacing.xl, mb: spacing.xl }}>
        {missing.map((key) => (
          <Box
            component="li"
            key={key}
            sx={{ ...typo.mono, color: colors.textPrimary, mb: spacing.xs }}
          >
            {key}
          </Box>
        ))}
      </Box>
      <Typography sx={{ ...typo.secondaryText, color: colors.textSecondary }}>
        Add them to <code>.env</code> (or the per-environment file such as <code>.env.dev01</code>)
        at the repo root and restart the dev server. To run without Auth0 for local development,
        also unset <code>REACT_APP_AUTH0_CLIENT_ID</code>.
      </Typography>
    </Paper>
  </Box>
);

export default AuthConfigErrorPage;
